/**
 * `TopBar` — ported from `Foyer_Dashboard_CURRENT.jsx` line 525.
 *
 * Phase 1 cuts (per ADR-0003 §"Phase-2 tab disablement strategy"):
 *   - Phase-2 tabs (Dashboard, Resolution, Visibility, Guardrails, Benchmarks,
 *     Live Agent) render with `aria-disabled="true"`, reduced opacity, no
 *     `onClick`, and a Tooltip reading "Available in Phase 2 — Transactional
 *     mode".
 *   - Disputes badge on Resolution is omitted (the disputes state lives in
 *     the Phase-2 Resolution view).
 */

import { AnimatePresence, motion, useAnimationControls, useReducedMotion } from 'framer-motion';
import {
  Briefcase,
  Building2,
  ChevronDown,
  ChevronsUpDown,
  CreditCard,
  LogOut,
  Menu,
  Moon,
  Plus,
  Settings as SettingsIcon,
  Sun,
} from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import type { User } from '@sill/types';

import { useApi } from '../../api/api-context.js';
import { usePendingEscalationsStore } from '../../app/pending-escalations-context.js';
import { useAgency } from '../../app/agency-context.js';
import { useAuth } from '../../app/auth-context.js';
import { useSites } from '../../app/providers.js';
import { AccountSwitcher } from './AccountSwitcher.js';
import { formatDisplayName } from '../../lib/format.js';
import { isLiveSite, siteModeDetailLabel, siteModeLabel } from '../../lib/site-state.js';
import type { View } from '../../app/view-router.js';
import { PHASE_2_VIEWS } from '../../app/view-router.js';
import { useC, useTheme } from '../../lib/theme.js';
import { PopoverMenu, StatusDot, Tooltip } from '../atoms/index.js';
import { type TabDef, useSiteTabs } from './site-tabs.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

const PHASE_2_TOOLTIP = 'Available in Phase 2 — Transactional mode';

/**
 * Horizontal-padding tier shared across the nav button + the hidden
 * measurement + More-probe rows. Keeps the live DOM width in lock-
 * step with the measurement row so computeFit never under- or
 * over-reserves. See the compactPadding JSDoc above `SiteTabs`.
 */
function padClass(tier: 0 | 1 | 2, extra = ''): string {
  const pad =
    tier === 2 ? 'px-1.5 py-2'
    : tier === 1 ? 'px-2 py-2'
    : 'px-2.5 lg:px-3 py-2';
  return extra.length > 0 ? `${pad} ${extra}` : pad;
}

export { SITE_TABS } from './site-tabs.js';

/**
 * Animated pending-escalation count badge on the Resolution tab
 * (founder 2026-07-02). Motion spec from the UI pass:
 *   - INCREMENT (a new held mandate arrived) → a lime pop (scale 1.18)
 *     plus a single expanding ring — draws the eye once, then stops.
 *   - DECREMENT (one just resolved) → a subtler inward dip (scale 0.88)
 *     with a brief amber tick, no ring — reads as progress consumed.
 *   - The number itself crossfades vertically; the slide direction
 *     encodes up vs down. Mono font keeps the glyph box stable.
 *   - Appears / disappears via AnimatePresence so the nav label settles
 *     smoothly across 0↔1 instead of snapping.
 *   - Honors prefers-reduced-motion: color-only acknowledgment, instant
 *     digit swap, no scale/ring/slide.
 *
 * Resting fill is kept LIME to match the shipped badge. The UI pass
 * recommended amber-at-rest as a semantic correction (a pending item is
 * an *escalate* state); that's left as a one-constant swap pending
 * founder sign-off (§8 — propose UI changes separately).
 */
function EscalationBadge({ count }: { count: number }): JSX.Element {
  const C = useC();
  const reduce = useReducedMotion() ?? false;
  const controls = useAnimationControls();
  const prevRef = useRef(count);
  const [ring, setRing] = useState(false);

  const RESTING = C.lime;
  const INCREMENT_FLASH = '#EBFF8F'; // a lighter lime — "more, and new"
  const DECREMENT_FLASH = C.amber; //   a brief amber tick — "one cleared"

  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = count;
    // No motion on mount (prev === count) or when the badge is empty.
    if (count === prev || count === 0) return;
    const up = count > prev;
    if (reduce) {
      void controls.start({
        backgroundColor: [up ? INCREMENT_FLASH : DECREMENT_FLASH, RESTING],
        transition: { duration: 0.15, ease: 'linear' },
      });
      return;
    }
    if (up) setRing(true);
    void controls.start(
      up
        ? {
            scale: [1, 1.18, 1],
            backgroundColor: [RESTING, INCREMENT_FLASH, RESTING],
            transition: { duration: 0.32, ease: 'easeOut' },
          }
        : {
            scale: [1, 0.88, 1],
            backgroundColor: [RESTING, DECREMENT_FLASH, RESTING],
            transition: { duration: 0.26, ease: 'easeOut' },
          },
    );
  }, [count, reduce, controls, RESTING, INCREMENT_FLASH, DECREMENT_FLASH]);

  // Slide direction for the digit crossfade — recomputed at render off the
  // not-yet-updated ref, so it reflects the direction of the pending change.
  const up = count >= prevRef.current;
  const label = count > 99 ? '99+' : String(count);

  return (
    <AnimatePresence initial={false}>
      {count > 0 ? (
        <motion.span
          key="escalation-badge"
          initial={reduce ? { opacity: 1 } : { opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          style={{ position: 'relative', display: 'inline-flex', marginLeft: 6, verticalAlign: 'middle' }}
        >
          {ring && !reduce ? (
            <motion.span
              aria-hidden
              initial={{ scale: 1, opacity: 0.35 }}
              animate={{ scale: 1.6, opacity: 0 }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              onAnimationComplete={() => setRing(false)}
              style={{
                position: 'absolute',
                inset: 0,
                background: RESTING,
                borderRadius: 2,
                pointerEvents: 'none',
              }}
            />
          ) : null}
          <motion.span
            aria-label={`${count} pending escalations`}
            animate={controls}
            style={{
              position: 'relative',
              padding: '1px 6px',
              background: RESTING,
              color: C.ink,
              borderRadius: 2,
              fontFamily: monoFont,
              fontSize: 10,
              fontWeight: 600,
              lineHeight: 1.4,
              overflow: 'hidden',
              display: 'inline-block',
            }}
          >
            <AnimatePresence mode="popLayout" initial={false}>
              <motion.span
                key={label}
                initial={reduce ? false : { opacity: 0, y: up ? 4 : -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduce ? { opacity: 0 } : { opacity: 0, y: up ? -4 : 4 }}
                transition={{ duration: 0.18, ease: 'easeOut' }}
                style={{ display: 'inline-block' }}
              >
                {label}
              </motion.span>
            </AnimatePresence>
          </motion.span>
        </motion.span>
      ) : null}
    </AnimatePresence>
  );
}

/**
 * `SiteTabs` — the merchant navigation row with priority-overflow
 * (§3.3). A ResizeObserver measures each tab's natural width once, then
 * on every container resize decides which tabs fit inline and which
 * fold into a trailing "More ▾" dropdown. Guarantees:
 *
 *  - `header.scrollWidth <= header.clientWidth` at every width ≥ 768 px
 *    on an agency account (no horizontal overflow).
 *  - The nav row never grows a vertical scrollbar (`overflow-y: hidden`
 *    with no negative margins — the old AgencyTabs vertical scrollbar
 *    came from `-1px` margin inside `overflow-x: auto`).
 *  - The active tab stays visible; if the active tab overflows, "More"
 *    renders in the active style and labels itself "More · {label}".
 *  - The Resolution escalation badge rides on whichever representation
 *    the Resolution tab currently takes (inline button or More menu).
 */
function SiteTabs({
  view,
  setView,
  pendingEscalations,
  tabs,
  compactPadding,
}: {
  view: View;
  setView: (v: View) => void;
  pendingEscalations: number;
  tabs: readonly TabDef[];
  /** Tab-item horizontal padding tier (founder 2026-10-20 — remove
   *  slack so 9 tabs fit at 1280 / 1152 with an agency switcher).
   *   - 0 : default 10/12 px (large viewports ≥ 1440).
   *   - 1 : 8 px (laptop widths 1200–1439).
   *   - 2 : 6 px (narrow laptops < 1200). */
  compactPadding: 0 | 1 | 2;
}): JSX.Element {
  const C = useC();
  const navRef = useRef<HTMLDivElement | null>(null);
  const measureRef = useRef<HTMLDivElement | null>(null);
  const moreBtnRef = useRef<HTMLButtonElement | null>(null);
  /** Hidden "More · <label>" probe row — the "More" button's rendered
   *  width when the active tab lives in overflow. Previously a fixed
   *  72 px (just "More ▾") that overflowed by 100-plus px when the
   *  label was "Settings" or "Agent readiness" and the org-switcher +
   *  site-switcher were both visible on 1024/1280 widths — the right
   *  side of the nav clipped "Settings" to "Setting" and "Agent
   *  readiness" to "Agent readines" (founder bug 2026-10-20 Report 4).
   */
  const moreMeasureRef = useRef<HTMLDivElement | null>(null);
  const [visibleCount, setVisibleCount] = useState<number>(tabs.length);
  const [moreOpen, setMoreOpen] = useState(false);

  // Measure every tab's natural width once; recompute visibleCount on
  // container resize AND on left/right sibling resize (the parent
  // header is `flex items-center justify-between`, so when the left
  // group grows — org switcher + site switcher — this nav shrinks).
  // The hidden measure row is identical in styling to the real row
  // but renders ALL tabs; we read its children widths and the
  // available width on each resize.
  useLayoutEffect(() => {
    const nav = navRef.current;
    const measure = measureRef.current;
    const moreMeasure = moreMeasureRef.current;
    if (!nav || !measure) return;

    const GAP = 2; // flex gap-0.5 ≈ 2 px
    const FIT_SAFETY_PX = 8; // small guard against sub-pixel + scrollbar rounding.

    const computeFit = (): void => {
      const children = Array.from(measure.children) as HTMLElement[];
      const widths = children.map((el) => el.getBoundingClientRect().width);
      const available = nav.getBoundingClientRect().width - FIT_SAFETY_PX;
      if (available <= 0 || widths.length === 0) return;

      // Try fitting all N (no overflow button needed).
      const totalAll = widths.reduce((a, w) => a + w, 0) + GAP * (widths.length - 1);
      if (totalAll <= available) {
        setVisibleCount(widths.length);
        return;
      }

      // Fit k ≤ N - 1 inline, reserve room for the More button.
      //
      // Founder 2026-10-20: the previous reserve used `max(all probe
      // widths)` including "More · Agent readiness" (~170 px), which
      // ate up to 100 px of slack whenever the active tab wasn't the
      // longest one — pushing otherwise-fitting tabs into overflow at
      // 1280 with an agency switcher visible. The reserve is now the
      // bare "More ▾" probe (index 0 of `moreMeasure`); if the active
      // tab turns out to live in overflow the button's `More · <label>`
      // form is wider than the reserve, but that's a layout decision
      // the browser handles (nav is `overflow: hidden` and the live
      // button sits in a `flex: 0 0 auto` wrapper, so the inline tabs
      // compress visually without re-laying out — never a dropped tab).
      let moreWidth = 72; // conservative floor if the probe row isn't mounted yet
      if (moreMeasure !== null) {
        const bareProbe = moreMeasure.children[0] as HTMLElement | undefined;
        if (bareProbe !== undefined) {
          moreWidth = bareProbe.getBoundingClientRect().width;
        }
      }

      let used = moreWidth + GAP;
      let k = 0;
      for (let i = 0; i < widths.length; i++) {
        const w = widths[i] ?? 0;
        const next = used + w + (k > 0 ? GAP : 0);
        if (next <= available) {
          used = next;
          k += 1;
        } else {
          break;
        }
      }
      setVisibleCount(k);
    };

    computeFit();
    // jsdom (unit tests) does not implement ResizeObserver — treat it
    // as unavailable and fall back to a one-shot measurement. In the
    // browser, ResizeObserver is universal (every target browser since
    // 2019) so the live path is always observed.
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => computeFit());
    ro.observe(nav);
    // Also observe the parent flex row so growth on the left (org +
    // site switcher) shrinks the available nav width before any gap
    // is clipped on the right. Without this, the nav's own width can
    // stay the same (nothing changed inside it) while the parent's
    // justify-between pushes the right group off-screen.
    const parent = nav.parentElement;
    if (parent !== null) ro.observe(parent);
    return () => ro.disconnect();
  }, [tabs]);

  // Outside-click + Escape close is owned by `PopoverMenu` now that
  // the dropdown renders through a portal. Nothing to wire here.

  const inlineTabs = useMemo(
    () => tabs.slice(0, visibleCount),
    [tabs, visibleCount],
  );
  const overflowTabs = useMemo(
    () => tabs.slice(visibleCount),
    [tabs, visibleCount],
  );
  const activeOverflowed = overflowTabs.some((t) => t.id === view);
  const activeOverflowLabel = overflowTabs.find((t) => t.id === view)?.label ?? '';

  const renderTab = (t: TabDef, inMenu: boolean): JSX.Element => {
    const active = view === t.id;
    const disabled = PHASE_2_VIEWS.has(t.id);
    if (inMenu) {
      return (
        <button
          type="button"
          key={t.id}
          role="menuitem"
          disabled={disabled}
          onClick={
            disabled
              ? undefined
              : () => {
                  setMoreOpen(false);
                  setView(t.id);
                }
          }
          aria-disabled={disabled || undefined}
          aria-current={active ? 'page' : undefined}
          className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left"
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            color: active ? C.text : C.muted,
            background: active ? C.surface2 : 'transparent',
            opacity: disabled ? 0.45 : 1,
            cursor: disabled ? 'not-allowed' : 'pointer',
            border: 'none',
          }}
        >
          <span>{t.label}</span>
          {t.id === 'resolution' ? <EscalationBadge count={pendingEscalations} /> : null}
        </button>
      );
    }
    const button = (
      <button
        type="button"
        key={t.id}
        onClick={disabled ? undefined : () => setView(t.id)}
        aria-disabled={disabled || undefined}
        aria-current={active ? 'page' : undefined}
        className={padClass(compactPadding, 'relative')}
        style={{
          fontFamily: sansFont,
          fontSize: 12,
          fontWeight: 500,
          color: active ? C.text : C.muted,
          background: active ? C.surface2 : 'transparent',
          border: `1px solid ${active ? C.border : 'transparent'}`,
          borderRadius: 2,
          letterSpacing: -0.1,
          whiteSpace: 'nowrap',
          opacity: disabled ? 0.45 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
          flex: '0 0 auto',
        }}
      >
        {t.label}
        {t.id === 'resolution' ? <EscalationBadge count={pendingEscalations} /> : null}
      </button>
    );
    return disabled ? (
      <Tooltip key={t.id} content={PHASE_2_TOOLTIP} side="bottom">
        {button}
      </Tooltip>
    ) : (
      button
    );
  };

  return (
    <nav
      ref={navRef}
      data-testid="site-tabs-nav"
      className="hidden md:flex items-center gap-0.5 relative"
      aria-label="Site navigation"
      style={{
        flex: '1 1 auto',
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      {/* Hidden measurement row — same styling, all tabs always rendered.
          `aria-hidden` + visually offscreen so screen readers never see it. */}
      <div
        ref={measureRef}
        aria-hidden
        style={{
          position: 'absolute',
          left: -10000,
          top: 0,
          display: 'flex',
          gap: 2,
          visibility: 'hidden',
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        {tabs.map((t) => (
          <span
            key={`measure-${t.id}`}
            className={padClass(compactPadding)}
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              fontWeight: 500,
              border: `1px solid transparent`,
              whiteSpace: 'nowrap',
            }}
          >
            {t.label}
          </span>
        ))}
      </div>
      {/* Hidden "More · <label>" probe row. One span per possible
          overflow form (bare "More ▾" plus "More · <label>" per tab);
          `computeFit` reads the max width so the inline/overflow split
          always leaves room for the active label's rendered width
          (founder bug 2026-10-20 Report 4). */}
      <div
        ref={moreMeasureRef}
        aria-hidden
        style={{
          position: 'absolute',
          left: -10000,
          top: 60,
          display: 'flex',
          gap: 2,
          visibility: 'hidden',
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        {/* Each probe mirrors the real More button's padding + layout.
            The chevron + badge add roughly 24 px which we approximate
            with the inline `paddingRight`. Keep in sync with the live
            button's `className` + style below. */}
        <span
          className={padClass(compactPadding)}
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            fontWeight: 500,
            border: `1px solid transparent`,
            whiteSpace: 'nowrap',
            paddingRight: 24,
          }}
        >
          More
        </span>
        {tabs.map((t) => (
          <span
            key={`more-probe-${t.id}`}
            className={padClass(compactPadding)}
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              fontWeight: 500,
              border: `1px solid transparent`,
              whiteSpace: 'nowrap',
              paddingRight: 24,
            }}
          >
            More · {t.label}
          </span>
        ))}
      </div>

      {inlineTabs.map((t) => renderTab(t, false))}

      {overflowTabs.length > 0 ? (
        <div style={{ flex: '0 0 auto' }}>
          <button
            type="button"
            ref={moreBtnRef}
            data-testid="site-tabs-more"
            onClick={() => setMoreOpen((v) => !v)}
            aria-expanded={moreOpen}
            aria-haspopup="menu"
            className={padClass(compactPadding, 'flex items-center gap-1')}
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              fontWeight: 500,
              color: activeOverflowed ? C.text : C.muted,
              background: activeOverflowed ? C.surface2 : 'transparent',
              border: `1px solid ${activeOverflowed ? C.border : 'transparent'}`,
              borderRadius: 2,
              letterSpacing: -0.1,
              whiteSpace: 'nowrap',
              cursor: 'pointer',
            }}
          >
            {activeOverflowed ? `More · ${activeOverflowLabel}` : 'More'}
            <ChevronDown size={11} color={C.muted} />
            {activeOverflowed && view === 'resolution' ? (
              <EscalationBadge count={pendingEscalations} />
            ) : null}
          </button>
          {/* Portal-rendered so the menu sits above the sticky TopBar
              and the page frame — the previous `position: absolute`
              dropdown clipped behind the content on narrow widths
              (founder report 2026-10-11). */}
          <PopoverMenu
            open={moreOpen}
            onClose={() => setMoreOpen(false)}
            anchorRef={moreBtnRef}
            align="end"
            minWidth={200}
            testid="site-tabs-more-menu"
            ariaLabel="More site tabs"
          >
            {overflowTabs.map((t) => renderTab(t, true))}
          </PopoverMenu>
        </div>
      ) : null}
    </nav>
  );
}

export interface TopBarProps {
  view: View;
  setView: (v: View) => void;
  onMobileMenu: () => void;
}

export function TopBar({ view, setView, onMobileMenu }: TopBarProps): JSX.Element {
  const C = useC();
  const { theme, setTheme } = useTheme();
  const { activeSite, setActiveSite, sites } = useSites();
  const api = useApi();
  const { features, acting, actingWhiteLabel } = useAgency();

  // Founder 2026-10-20 — squeeze cascade. On agency accounts the full
  // AccountSwitcher trigger (icon + CLIENT kicker + name) can eat
  // ~220 px of the top bar, which drops otherwise-fitting nav items
  // into "More" at 1280 / 1440. The cascade shrinks the AccountSwitcher
  // first (ellipsis → icon-only) and tightens the site switcher's
  // domain cap second, before SiteTabs's own overflow logic kicks in.
  //
  // The thresholds are measured against the full width of the sticky
  // header's inner row — the ResizeObserver fires on every window
  // resize, including the mount burst in tests, so the state settles
  // in one tick. The breakpoints are chosen so a 9-tab site at 1280
  // with an agency switcher visible lands in `ellipsis` (saves ~80 px)
  // or `icon` (saves ~160 px) and all tabs fit inline.
  const headerRowRef = useRef<HTMLDivElement | null>(null);
  const [headerWidth, setHeaderWidth] = useState<number>(() => {
    if (typeof window === 'undefined') return 0;
    return window.innerWidth;
  });
  useLayoutEffect(() => {
    const el = headerRowRef.current;
    if (el === null) return;
    // Seed with the real width; the live listener below handles updates.
    setHeaderWidth(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      if (w > 0) setHeaderWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const hasAgencyTrigger = features?.agency === true || acting !== null;
  const inSiteContext = activeSite !== null;
  // Thresholds (header-row inner width — padding already excluded by
  // the observer's `contentRect`). Founder target 2026-10-20: with a
  // site context + an agency switcher visible, all 9 tabs fit inline
  // down to ~1150 px; below that the "More" overflow begins. The
  // density cascade shrinks the switcher first — ellipsis then icon
  // — so the switcher gives space before nav tabs do. Without an
  // agency switcher the full-text form always fits.
  const accountSwitcherDensity: 'full' | 'ellipsis' | 'icon' =
    !hasAgencyTrigger || !inSiteContext
      ? 'full'
      : headerWidth > 0 && headerWidth < 1300
        ? 'icon'
        : headerWidth > 0 && headerWidth < 1500
          ? 'ellipsis'
          : 'full';
  // Site switcher's own ellipsis cap. Default 160; tighten in two
  // steps so the domain truncates (not disappears) before any nav
  // item is pushed into More.
  const siteSwitcherMaxWidth: number =
    headerWidth > 0 && headerWidth < 1300
      ? 100
      : headerWidth > 0 && headerWidth < 1400
        ? 130
        : 160;
  // Tiered nav-tab padding below 1440 so all 9 tabs fit inline on
  // laptop widths (founder target 2026-10-20 — More only below ~1150).
  //   0  ≥ 1440   10/12 px (full comfort).
  //   1  1200–1439 8 px (laptop recovery of ~36 px across 9 tabs).
  //   2  < 1200    6 px (narrow laptop recovery of another ~36 px).
  const navCompactPadding: 0 | 1 | 2 =
    headerWidth === 0 || headerWidth >= 1440
      ? 0
      : headerWidth >= 1200
        ? 1
        : 2;
  // Below 1300 the agency wordmark's display name (e.g. "Fieldstone
  // Digital", "Northwind Digital") can eat another ~180 px of header
  // slot. Truncate it with a tooltip to recover it; the Sill
  // wordmark itself has a fixed glyph and does not need the cap.
  // Tighter at < 1200 so 9 tabs still fit at 1152 (founder target:
  // More only below ~1150).
  const agencyWordmarkMaxWidth: number | undefined =
    actingWhiteLabel === null
      ? undefined
      : headerWidth > 0 && headerWidth < 1200
        ? 80
        : headerWidth > 0 && headerWidth < 1300
          ? 120
          : undefined;
  // Pending-escalation badge on the Resolution tab. Reads the shared,
  // site-scoped store (not its own poller) so it stays in lockstep with
  // the Resolution queue — new escalations surface within the store's
  // poll window and a resolve decrements it instantly (founder 2026-07-02).
  const { count: pendingEscalations } = usePendingEscalationsStore();
  const siteTabs = useSiteTabs(view);
  const auth = useAuth();
  // Mutex-aware setters for the two inline dropdowns (site switcher +
  // account menu). Opening one closes the other. Also: both close on
  // outside pointerdown, Escape, and route change — wired below.
  const [siteMenuOpen, setSiteMenuOpenRaw] = useState(false);
  const [acctMenuOpen, setAcctMenuOpenRaw] = useState(false);
  const setSiteMenuOpen = (next: boolean | ((v: boolean) => boolean)): void => {
    setSiteMenuOpenRaw((prev) => {
      const n = typeof next === 'function' ? next(prev) : next;
      if (n) setAcctMenuOpenRaw(false);
      return n;
    });
  };
  const setAcctMenuOpen = (next: boolean | ((v: boolean) => boolean)): void => {
    setAcctMenuOpenRaw((prev) => {
      const n = typeof next === 'function' ? next(prev) : next;
      if (n) setSiteMenuOpenRaw(false);
      return n;
    });
  };
  const siteMenuWrapRef = useRef<HTMLDivElement | null>(null);
  const acctMenuWrapRef = useRef<HTMLDivElement | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  // Close dropdowns on view change (route change). The dashboard is a
  // single-page `view` router; `view` turning over is our route change.
  useEffect(() => {
    setSiteMenuOpenRaw(false);
    setAcctMenuOpenRaw(false);
  }, [view]);

  // Outside-pointerdown closes the open dropdown. Fires on `mousedown`
  // (not `click`) so the menu disappears the moment the user commits
  // to another spot — matches the AccountSwitcher's own close rule.
  useEffect(() => {
    if (!siteMenuOpen && !acctMenuOpen) return;
    const onDown = (e: MouseEvent): void => {
      const target = e.target;
      if (!(target instanceof Node)) return;
      if (siteMenuOpen && siteMenuWrapRef.current?.contains(target)) return;
      if (acctMenuOpen && acctMenuWrapRef.current?.contains(target)) return;
      setSiteMenuOpenRaw(false);
      setAcctMenuOpenRaw(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setSiteMenuOpenRaw(false);
        setAcctMenuOpenRaw(false);
      }
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [siteMenuOpen, acctMenuOpen]);

  // Role gate for "Add new site" (server enforces owner/admin on POST
  // /v1/sites; the dashboard hides the affordance as UX — defense-in-
  // depth, not the security boundary).
  const accountRole =
    auth.state.kind === 'authenticated' ? auth.state.user.role : null;
  const canAddSite = accountRole === 'owner' || accountRole === 'admin';

  useEffect(() => {
    let cancelled = false;
    void api.auth.currentUser().then((u) => {
      if (!cancelled) setCurrentUser(u);
    });
    return () => {
      cancelled = true;
    };
  }, [api]);

  const initial = currentUser?.name?.[0]?.toUpperCase() ?? '?';

  return (
    <header
      className="sticky top-0 z-40 px-4 sm:px-6 py-3"
      style={{
        background: theme === 'dark' ? 'rgba(10,10,10,0.85)' : 'rgba(250,247,240,0.85)',
        backdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${C.borderSoft}`,
      }}
    >
      <div className="flex items-center justify-between gap-3" ref={headerRowRef}>
        <div
          className="flex items-center gap-3"
          // The left group used to be `flexShrink: 0`, which kept the
          // AccountSwitcher's full-text form from giving ground when
          // the nav tabs needed room. The density cascade above now
          // owns the squeeze order (ellipsis → icon → tighter site
          // switcher), so the group itself is allowed to shrink to its
          // intrinsic contents — never below, since every child has a
          // minWidth that respects its own hit-target floor.
          style={{ flexShrink: 1, minWidth: 0 }}
          data-testid="shell-left-group"
        >
          <button
            type="button"
            onClick={() => {
              setActiveSite(null);
              setView('sites');
            }}
            className="flex items-center flex-shrink-0 sill-brand-link"
            aria-label={
              actingWhiteLabel !== null
                ? `${actingWhiteLabel.display_name} home`
                : 'Sill home'
            }
            data-testid="shell-wordmark"
          >
            {actingWhiteLabel !== null ? (
              // ADR-0240 Slice 2c — acting-session agency chrome. The
              // agency logo (if configured) + display name replaces
              // the Sill wordmark during an acting session when the
              // acting agency's white_label toggle is on. The ADR
              // acting-banner (ADR-0237) stays Sill-branded so the
              // user never loses the "this is Sill-with-agency-brand"
              // affordance.
              <span
                className="flex items-center gap-2"
                data-testid="shell-wordmark-agency"
              >
                {actingWhiteLabel.logo_url !== null ? (
                  <img
                    src={actingWhiteLabel.logo_url}
                    alt={actingWhiteLabel.display_name}
                    referrerPolicy="no-referrer"
                    loading="lazy"
                    decoding="async"
                    style={{
                      height: 24,
                      width: 'auto',
                      maxHeight: 24,
                      maxWidth: 160,
                      verticalAlign: 'middle',
                    }}
                  />
                ) : null}
                <span
                  // Founder 2026-10-20: cap the agency display name
                  // with ellipsis below 1300 px so a long white-label
                  // (e.g. "Fieldstone Digital", "Northwind Digital
                  // Partners") does not eat the space nav tabs need.
                  // The full name stays on `title` + the button's
                  // aria-label so it is still disclosed on hover and
                  // to screen readers.
                  title={agencyWordmarkMaxWidth !== undefined ? actingWhiteLabel.display_name : undefined} /* title-ok: full value of the ellipsised wordmark */
                  style={{
                    fontFamily: "'Geist', sans-serif",
                    fontSize: 18,
                    fontWeight: 600,
                    color: C.text,
                    letterSpacing: -0.2,
                    ...(agencyWordmarkMaxWidth !== undefined
                      ? {
                          maxWidth: agencyWordmarkMaxWidth,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'inline-block',
                          verticalAlign: 'middle',
                        }
                      : {}),
                  }}
                >
                  {actingWhiteLabel.display_name}
                </span>
              </span>
            ) : (
              <span
                className="sill-wordmark"
                style={{ fontSize: 36, color: C.text, display: 'inline-block' }}
              >
                sill<span className="sill-dot">.</span>
              </span>
            )}
          </button>

          {/* ADR-0237 — agency account switcher. Hidden unless the home
              session is agency-enabled OR we're currently acting. The
              `density` prop drives the squeeze cascade (founder
              2026-10-20): it shrinks before site tabs fall into More. */}
          <div className="hidden sm:block">
            <AccountSwitcher density={accountSwitcherDensity} />
          </div>

          {inSiteContext && activeSite ? (
            <>
              <span
                className="hidden sm:inline"
                style={{ color: C.dim, fontSize: 14 }}
                aria-hidden="true"
              >
                /
              </span>
              <div className="relative" ref={siteMenuWrapRef} data-testid="shell-site-switcher">
                <button
                  type="button"
                  onClick={() => setSiteMenuOpen(!siteMenuOpen)}
                  className="flex items-center gap-2 px-2 sm:px-3 py-1.5"
                  style={{
                    background: C.surface2,
                    border: `1px solid ${C.border}`,
                    borderRadius: 2,
                  }}
                  aria-expanded={siteMenuOpen}
                >
                  <StatusDot color={isLiveSite(activeSite) ? C.lime : C.amber} />
                  <span
                    title={activeSite.domain} /* title-ok: full value of the ellipsised domain */
                    style={{
                      fontFamily: sansFont,
                      fontSize: 13,
                      color: C.text,
                      fontWeight: 500,
                      // Founder 2026-10-20: `siteSwitcherMaxWidth`
                      // tightens as the header narrows (160 → 130 →
                      // 100), so the domain truncates before any nav
                      // item is pushed into "More".
                      maxWidth: siteSwitcherMaxWidth,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {activeSite.domain}
                  </span>
                  <ChevronsUpDown size={11} color={C.muted} className="flex-shrink-0" />
                </button>
                <AnimatePresence>
                  {siteMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="absolute top-full left-0 mt-2 z-50"
                      style={{
                        width: 320,
                        background: C.surface,
                        border: `1px solid ${C.border}`,
                        borderRadius: 3,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                      }}
                    >
                      <div
                        className="px-3 py-2"
                        style={{
                          fontFamily: monoFont,
                          fontSize: 9,
                          color: C.muted,
                          letterSpacing: 2,
                          borderBottom: `1px solid ${C.borderSoft}`,
                        }}
                      >
                        YOUR SITES · {sites.length}
                      </div>
                      {sites.map((s) => (
                        <button
                          key={s.site_id}
                          type="button"
                          onClick={() => {
                            setActiveSite(s);
                            setSiteMenuOpen(false);
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 text-left"
                          style={{
                            background: s.site_id === activeSite.site_id ? C.surface2 : 'transparent',
                            borderBottom: `1px solid ${C.borderSoft}`,
                          }}
                        >
                          <StatusDot color={isLiveSite(s) ? C.lime : C.amber} />
                          <div className="flex-1 min-w-0">
                            <div
                              style={{
                                fontFamily: sansFont,
                                fontSize: 13,
                                color: C.text,
                                fontWeight: 500,
                              }}
                            >
                              {s.domain}
                            </div>
                            <div
                              style={{
                                fontFamily: monoFont,
                                fontSize: 10,
                                color: C.muted,
                                marginTop: 2,
                              }}
                            >
                              {/* Founder 2026-10-20: show each site's real
                                  mode (Discovery vs Transactions ready)
                                  instead of a single generic sub-line. The
                                  verifying / awaiting-traffic states are
                                  still surfaced for sites that are not yet
                                  observed. Founder 2026-10-21: when a site
                                  is still Discovery but has an active
                                  payment-rail connector, use the detail
                                  label ("Stripe connected · not taking
                                  orders yet") so the switcher doesn't
                                  falsely read "nothing connected". */}
                              {(s.status !== 'discovery_active' && s.status !== 'transactional_active')
                                ? 'verifying...'
                                : !s.last_seen_at
                                  ? `${siteModeLabel(s).toLowerCase()} · awaiting traffic`
                                  : siteModeDetailLabel(s)}
                            </div>
                          </div>
                        </button>
                      ))}
                      {canAddSite ? (
                        <button
                          type="button"
                          onClick={() => {
                            setActiveSite(null);
                            setView('addSite');
                            setSiteMenuOpen(false);
                          }}
                          className="w-full flex items-center gap-2 px-3 py-2.5 text-left"
                          style={{
                            borderTop: `1px solid ${C.borderSoft}`,
                            fontFamily: sansFont,
                            fontSize: 13,
                            color: C.limeFg,
                            fontWeight: 500,
                          }}
                        >
                          <Plus size={13} /> Add new site
                        </button>
                      ) : (
                        <div
                          className="px-3 py-2.5"
                          style={{
                            borderTop: `1px solid ${C.borderSoft}`,
                            fontFamily: sansFont,
                            fontSize: 11,
                            color: C.muted,
                            lineHeight: 1.4,
                          }}
                        >
                          Only owners and admins can add sites.
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          setActiveSite(null);
                          setView('sites');
                          setSiteMenuOpen(false);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2.5 text-left"
                        style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}
                      >
                        <Building2 size={13} /> Manage all sites
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </>
          ) : null}
        </div>

        {inSiteContext ? (
          <SiteTabs
            view={view}
            setView={setView}
            pendingEscalations={pendingEscalations}
            tabs={siteTabs}
            compactPadding={navCompactPadding}
          />
        ) : null}

        <div className="flex items-center gap-2 flex-shrink-0">
          <Tooltip content={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`} side="bottom">
            <button
              type="button"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              aria-label="Toggle theme"
              className="hidden sm:flex w-8 h-8 items-center justify-center"
              style={{
                background: C.surface2,
                border: `1px solid ${C.border}`,
                borderRadius: 2,
              }}
            >
              {theme === 'dark' ? <Sun size={13} color={C.muted} /> : <Moon size={13} color={C.muted} />}
            </button>
          </Tooltip>

          <div className="relative hidden sm:block" ref={acctMenuWrapRef} data-testid="shell-account-menu">
            <button
              type="button"
              onClick={() => setAcctMenuOpen(!acctMenuOpen)}
              aria-label="Account menu"
              aria-expanded={acctMenuOpen}
              className="w-8 h-8 flex items-center justify-center"
              style={{
                background: C.lime,
                borderRadius: 2,
                color: C.ink,
                fontFamily: sansFont,
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              {initial}
            </button>
            <AnimatePresence>
              {acctMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="absolute top-full right-0 mt-2 z-50"
                  style={{
                    width: 240,
                    background: C.surface,
                    border: `1px solid ${C.border}`,
                    borderRadius: 3,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                  }}
                >
                  <div className="px-3 py-3" style={{ borderBottom: `1px solid ${C.borderSoft}` }}>
                    <div
                      style={{
                        fontFamily: sansFont,
                        fontSize: 13,
                        color: C.text,
                        fontWeight: 500,
                      }}
                    >
                      {currentUser?.name ? formatDisplayName(currentUser.name) : '…'}
                    </div>
                    <div
                      style={{ fontFamily: sansFont, fontSize: 11, color: C.muted, marginTop: 2 }}
                    >
                      {currentUser?.email ?? ''}
                    </div>
                  </div>
                  {[
                    {
                      icon: Building2,
                      label: 'All sites',
                      cb: () => {
                        setActiveSite(null);
                        setView('sites');
                      },
                    },
                    // Agency Portal v2 Slice 4 — one entry replaces
                    // the five Clients / Reports / Referral / Scan
                    // widget / White label entries. The portal renders
                    // the matching section through `?section=…`; the
                    // server still gates each surface.
                    ...(features?.agency === true && acting === null
                      ? [
                          {
                            icon: Briefcase,
                            label: 'Agency Portal',
                            cb: () => {
                              setActiveSite(null);
                              setView('agencyPortal');
                            },
                          },
                        ]
                      : []),
                    {
                      icon: CreditCard,
                      label: 'Billing',
                      cb: () => {
                        setActiveSite(null);
                        // Billing now lives as a tab in the account view; deep-
                        // link straight to it (ADR-0155 IA consolidation).
                        try {
                          const url = new URL(window.location.href);
                          url.searchParams.set('account_tab', 'billing');
                          window.history.replaceState(null, '', url.toString());
                        } catch {
                          /* enhancement only */
                        }
                        setView('account');
                      },
                    },
                    {
                      icon: SettingsIcon,
                      label: 'Account settings',
                      cb: () => {
                        setActiveSite(null);
                        setView('account');
                      },
                    },
                  ].map((item) => (
                    <button
                      key={item.label}
                      type="button"
                      onClick={() => {
                        item.cb();
                        setAcctMenuOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-left"
                      style={{ fontFamily: sansFont, fontSize: 13, color: C.text }}
                    >
                      <item.icon size={13} color={C.muted} /> {item.label}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      setAcctMenuOpen(false);
                      void auth.signOut();
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-left"
                    style={{
                      borderTop: `1px solid ${C.borderSoft}`,
                      fontFamily: sansFont,
                      fontSize: 13,
                      color: C.muted,
                    }}
                  >
                    <LogOut size={13} /> Sign out
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <button
            type="button"
            onClick={onMobileMenu}
            aria-label="Open menu"
            className="md:hidden w-9 h-9 flex items-center justify-center"
            style={{
              background: C.surface2,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
            }}
          >
            <Menu size={16} color={C.text} />
          </button>
        </div>
      </div>
    </header>
  );
}
